import { AccountLoadError } from '@components/accountLoadError';
import { LoaderSpinner } from '@components/loader/spinner';
import {
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from '@components/ui/drawer';
import { usePanel } from '@hooks/usePanel';
import { OfficeTab } from '@root/globalTypes';
import { cn } from '@utils/cn';
import { parseRoute } from '@utils/i18n';
import { kebabToCamelCase } from '@utils/textHelpers';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import { icons } from '../constants';
import { Footer } from '@components/footer';
import { MobileMenuProps } from './types';
import { CloseButton } from '@components/ui/closeButton';

export const MobileMenu = ({ open, setOpen, routes, tabError, accountError }: MobileMenuProps) => {
  const { t } = useTranslation('app');
  const { pathname } = useLocation();
  const { parseUrl } = usePanel();
  const close = () => setOpen(false);

  return (
    <DrawerRoot open={open} onOpenChange={setOpen} direction="bottom">
      <DrawerContent
        overlayClassName="z-[60] bg-[rgba(24,28,33,.45)]"
        className="max-h-[86vh] rounded-t-2xl border-0 pb-[calc(10px+env(safe-area-inset-bottom,0px))] shadow-[0_-8px_30px_rgba(0,0,0,.18)]">
        <DrawerHeader className="flex flex-row items-center justify-between border-b py-1.5 pl-[18px] pr-2">
          <DrawerTitle className="m-0 text-base font-bold text-neutral-800">
            {t('navigation.menu')}
          </DrawerTitle>
          <DrawerClose asChild>
            <CloseButton />
          </DrawerClose>
        </DrawerHeader>
        <div className="overflow-y-auto">
          {!routes.length &&
            (accountError ? (
              <AccountLoadError {...accountError} />
            ) : (
              <div className="flex justify-center p-4">
                <LoaderSpinner />
              </div>
            ))}
          <ul className="grid grid-cols-3 gap-1 px-2.5 pb-1 pt-2.5">
            {routes.map((route) => {
              const tab = kebabToCamelCase(route) as OfficeTab;
              return (
                <li key={route}>
                  <NavLink
                    onClick={(e) => {
                      close();
                      if (pathname !== `/${parseRoute(route)}`) return;
                      e.preventDefault();
                    }}
                    to={`/${parseRoute(parseUrl(route))}`}
                    className={({ isActive }) =>
                      cn(
                        'relative flex min-h-[78px] flex-col items-center justify-center gap-0.5 rounded-xl p-1.5 text-center text-xs font-semibold text-neutral-800 active:bg-neutral-50 [&>svg]:h-9 [&>svg]:w-9 [&>svg]:shrink-0 [&>svg]:text-neutral-600',
                        isActive && 'bg-primary-50 text-primary-500 [&>svg]:text-primary-500',
                      )
                    }>
                    {icons[tab]}
                    <span>{t(`navigation.${tab}`)}</span>
                    {tabError(tab) && (
                      <span className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-danger-400" />
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
        <Footer menu />
      </DrawerContent>
    </DrawerRoot>
  );
};
